import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Table, Input, Button, Tag, Space, Select, Row, Col, message, Form, Switch, InputNumber, Divider, Alert, Tooltip } from 'antd';
import { SearchOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { api } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_ADMIN } from '../utils/permissions';
import { fetchProductBrands, type ProductBrandDto } from '../services/productBrandService';
import { fetchProductsForDocumentsPaged, type ProductForDocumentDto } from '../services/productService';

interface Unit {
  id: number;
  name: string;
  symbol: string;
  baseRatio: number;
}

// Ürün birimleri için
interface ProductInfo {
  id: number;
  unitSetId: number;
  baseUnitId: number;
  units: Unit[];
  taxRate?: number; // Ürünün KDV oranı
  categoryId?: number;
  productBrandId?: number | null;
}

interface ProductRowData {
  key: string;
  productId: number;
  productCode: string;
  productName: string;
  variantId: number;
  sku: string;
  variantDescription: string;
  retailPrice?: number; // null/undefined olabilir
  wholesalePrice?: number; // null/undefined olabilir
  purchasePrice?: number; // null/undefined olabilir
  stock: number; // Tüm warehouse'lardaki toplam stok
  units: Unit[];
  baseUnitId: number;
  taxRate?: number; // Ürünün KDV oranı
  categoryId?: number;
  productBrandId?: number | null;
}

interface ProductRowDataWithType extends ProductRowData {
  rowType: 'single' | 'variant';
  parentKey?: string;
}

interface ParentRowData {
  key: string;
  rowType: 'parent';
  productId: number;
  productCode: string;
  productName: string;
  baseUnitId: number;
  units: Unit[];
  taxRate?: number;
  totalStock: number;
  variantCount: number;
  children: ProductRowDataWithType[];
  categoryId?: number;
}

type TableRowData = ParentRowData | ProductRowDataWithType;

interface VariantDefinition {
  id: number;
  name: string;
}

interface VariantValueDefinition {
  id: number;
  variantId: number;
  value: string;
  code?: string;
}

interface SelectedVariantOption {
  variantId: number;
  variantName: string;
  selectedValueId?: number;
}

interface ProductSelectModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (product: ProductRowData, selectedUnit: Unit, priceTypeId: number) => void;
  customerCategory?: string; // "Retail" veya "Wholesale"
  defaultTaxRate?: number;
  priceTypeId?: number; // Document type'a göre belirlenen fiyat tipi (1: Perakende, 2: Toptan, 3: Alış)
  addedItems?: Array<{
    productId?: number | null;
    variantId?: number | null;
  }>;
}

const ProductSelectModal: React.FC<ProductSelectModalProps> = ({ 
  visible, 
  onClose, 
  onSelect,
  customerCategory,
  defaultTaxRate,
  priceTypeId,
  addedItems = []
}) => {
  const [pageProducts, setPageProducts] = useState<ProductForDocumentDto[]>([]);
  const [productInfosError, setProductInfosError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(true);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState<'code' | 'name' | 'barcode'>('name');
  const [selectedProduct, setSelectedProduct] = useState<ProductRowDataWithType | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddForm] = Form.useForm();
  const [categories, setCategories] = useState<any[]>([]);
  const [unitSets, setUnitSets] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [variantDefinitions, setVariantDefinitions] = useState<VariantDefinition[]>([]);
  const [variantValueMap, setVariantValueMap] = useState<Record<number, VariantValueDefinition[]>>({});
  const [variantDataLoading, setVariantDataLoading] = useState(false);
  const quickAddIsVariantProduct = Form.useWatch('isVariantProduct', quickAddForm);
  const [variantModalVisible, setVariantModalVisible] = useState(false);
  const [variantModalProduct, setVariantModalProduct] = useState<ProductRowData | null>(null);
  const [variantModalLoading, setVariantModalLoading] = useState(false);
  const [variantModalStock, setVariantModalStock] = useState<number | null>(null);
  const [variantModalForm] = Form.useForm();
  const variantModalTransferStock = Form.useWatch('transferStock', variantModalForm);
  const [quickSelectedVariants, setQuickSelectedVariants] = useState<SelectedVariantOption[]>([]);
  const [modalSelectedVariants, setModalSelectedVariants] = useState<SelectedVariantOption[]>([]);
  const [isVariantDrawerVisible, setIsVariantDrawerVisible] = useState(false);
  const [variantDrawerType, setVariantDrawerType] = useState<'definition' | 'value'>('definition');
  const [variantDrawerPrimaryVariantId, setVariantDrawerPrimaryVariantId] = useState<number | null>(null);
  const [variantDrawerForm] = Form.useForm();
  const { baseCurrencySymbol } = useCurrency();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [productBrands, setProductBrands] = useState<ProductBrandDto[]>([]);
  const [selectedProductBrandId, setSelectedProductBrandId] = useState<number | null>(null);

  const { user } = useAuth();
  const roleCodes = user?.roleCodes ?? [];
  const canSeePurchasePrice = roleCodes.includes(ROLE_ADMIN);

  const addedItemKeys = useMemo(() => {
    const keys = new Set<string>();
    addedItems.forEach(item => {
      if (item.variantId && item.variantId !== 0) {
        keys.add(`variant-${item.variantId}`);
      } else if (item.productId) {
        keys.add(`product-${item.productId}`);
      }
    });
    return keys;
  }, [addedItems]);

  const isAlreadyAdded = useCallback((record: { productId: number; variantId: number }) => {
    if (record.variantId && record.variantId !== 0) {
      return addedItemKeys.has(`variant-${record.variantId}`);
    }
    return addedItemKeys.has(`product-${record.productId}`);
  }, [addedItemKeys]);

  const generateAutoBarcode = () => {
    const timestamp = Date.now().toString().slice(-9);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const first12 = (timestamp + random).slice(0, 12);

    let sum = 0;
    for (let i = 0; i < 12; i += 1) {
      const digit = parseInt(first12[i], 10);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return first12 + checkDigit;
  };

  const buildCategoryHierarchy = (cats: any[]) => {
    const map = new Map<number, any>();
    cats.forEach(cat => {
      map.set(cat.id, { ...cat, children: [] as any[] });
    });

    const roots: any[] = [];

    map.forEach(cat => {
      if (cat.parentId && map.has(cat.parentId)) {
        map.get(cat.parentId).children.push(cat);
      } else {
        roots.push(cat);
      }
    });

    const sorted: any[] = [];

    const traverse = (node: any, level: number) => {
      const hasChildren = node.children.length > 0;
      const prefix = level > 0 ? `${'— '.repeat(level)}` : '';
      sorted.push({
        ...node,
        hasChildren,
        displayName: `${prefix}${node.categoryName}`
      });

      node.children
        .sort((a: any, b: any) => a.categoryName.localeCompare(b.categoryName, 'tr'))
        .forEach((child: any) => traverse(child, level + 1));
    };

    roots
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'tr'))
      .forEach(root => traverse(root, 0));

    return sorted;
  };

  const productInfos = useMemo(() => {
    const map: Record<number, ProductInfo> = {};
    pageProducts.forEach((product) => {
      map[product.id] = {
        id: product.id,
        unitSetId: product.unitSetId,
        baseUnitId: product.baseUnitId,
        units: product.units || [],
        taxRate: product.taxRate,
        categoryId: product.categoryId,
        productBrandId: product.productBrandId ?? undefined,
      };
    });
    return map;
  }, [pageProducts]);

  const loadPagedProducts = useCallback(async (page: number, pageSize: number) => {
    try {
      setLoading(true);
      setProductInfosError(null);
      const result = await fetchProductsForDocumentsPaged({
        page,
        pageSize,
        search: searchText.trim() || undefined,
        searchType,
        categoryId: selectedCategoryId ?? undefined,
        productBrandId: selectedProductBrandId ?? undefined,
        inStockOnly,
      });
      setPageProducts(result.data ?? []);
      setPagination((prev) => ({
        ...prev,
        current: result.page ?? page,
        pageSize: result.pageSize ?? pageSize,
        total: result.totalCount ?? 0,
      }));
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.response?.data?.detail || 'Ürün listesi yüklenemedi';
      setProductInfosError(errMsg);
      setPageProducts([]);
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [
    searchText,
    searchType,
    selectedCategoryId,
    selectedProductBrandId,
    inStockOnly,
  ]);

  useEffect(() => {
    if (visible) {
      setProductInfosError(null);
      setPagination((prev) => ({ ...prev, current: 1 }));
      loadCategories();
      loadUnitSets();
      loadVariantDefinitions();
      fetchProductBrands().then(setProductBrands).catch(() => setProductBrands([]));
      setShowQuickAdd(false);
      quickAddForm.resetFields();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      loadPagedProducts(1, pagination.pageSize);
    }, searchText ? 350 : 0);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [
    visible,
    searchText,
    searchType,
    selectedCategoryId,
    selectedProductBrandId,
    inStockOnly,
    pagination.pageSize,
    loadPagedProducts,
  ]);

  useEffect(() => {
    if (!quickAddIsVariantProduct) {
      setQuickSelectedVariants([]);
      return;
    }

    if (quickAddIsVariantProduct && variantDefinitions.length > 0 && quickSelectedVariants.length === 0) {
      const firstVariant = variantDefinitions[0];
      const firstValue = variantValueMap[firstVariant.id]?.[0];
      setQuickSelectedVariants(firstVariant ? [{
        variantId: firstVariant.id,
        variantName: firstVariant.name,
        selectedValueId: firstValue?.id
      }] : []);
    }
  }, [quickAddIsVariantProduct, variantDefinitions, variantValueMap, quickSelectedVariants.length]);

  const isParentRow = (record: TableRowData): record is ParentRowData => record.rowType === 'parent';

  // Ürün bulunamadığında otomatik olarak hızlı ekle formunu göster
  useEffect(() => {
    if (searchText && pageProducts.length === 0 && !loading) {
      setShowQuickAdd(true);
      if (searchType === 'barcode') {
        quickAddForm.setFieldsValue({ barcode: searchText });
      } else if (searchType === 'code') {
        quickAddForm.setFieldsValue({ productCode: searchText });
      } else {
        quickAddForm.setFieldsValue({ productName: searchText });
      }
    } else if (pageProducts.length > 0) {
      setShowQuickAdd(false);
    }
  }, [pageProducts, searchText, loading, searchType]);

  const mapPageProductToRows = useCallback((product: ProductForDocumentDto): ProductRowData[] => {
    const effectiveDefaultTaxRate = defaultTaxRate ?? 20;
    const units = product.units || [];
    const hasVariants = (product.variants?.length ?? 0) > 0;

    if (hasVariants) {
      return product.variants.map((variant) => {
        const variantDescription = variant.variantValues
          .map((vv) => `${vv.variantName}: ${vv.variantValue}`)
          .join(', ');

        return {
          key: `${product.id}-${variant.id}`,
          productId: product.id,
          productCode: product.productCode,
          productName: product.productName,
          variantId: variant.id,
          sku: variant.sku || product.productCode,
          variantDescription,
          retailPrice: variant.price ?? undefined,
          wholesalePrice: variant.wholesalePrice ?? undefined,
          purchasePrice: variant.purchasePrice ?? undefined,
          stock: variant.stock,
          units,
          baseUnitId: product.baseUnitId,
          taxRate: product.taxRate ?? effectiveDefaultTaxRate,
          categoryId: product.categoryId,
          productBrandId: product.productBrandId ?? undefined,
        };
      });
    }

    return [{
      key: `${product.id}-0`,
      productId: product.id,
      productCode: product.productCode,
      productName: product.productName,
      variantId: 0,
      sku: product.productCode,
      variantDescription: 'Standart',
      retailPrice: product.retailPrice ?? undefined,
      wholesalePrice: product.wholesalePrice ?? undefined,
      purchasePrice: product.purchasePrice ?? undefined,
      stock: product.baseStock,
      units,
      baseUnitId: product.baseUnitId,
      taxRate: product.taxRate ?? effectiveDefaultTaxRate,
      categoryId: product.categoryId,
      productBrandId: product.productBrandId ?? undefined,
    }];
  }, [defaultTaxRate]);

  const handleSelect = () => {
    if (selectedProduct && selectedUnit) {
      // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
      const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
      onSelect(selectedProduct, selectedUnit, finalPriceTypeId);
      onClose();
      resetState();
    }
  };

  const handleAddWithoutClosing = (record: TableRowData) => {
    if (isParentRow(record)) {
      return;
    }

    const variantRecord = record as ProductRowDataWithType;

    if (isAlreadyAdded(variantRecord)) {
      message.warning('Bu ürün zaten fişte mevcut');
      return;
    }

    const baseUnit = variantRecord.units.find(u => u.id === variantRecord.baseUnitId) || variantRecord.units[0];

    if (!baseUnit) {
      message.error('Ürün birimi bulunamadı!');
      return;
    }

    // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
    const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
    onSelect(variantRecord, baseUnit, finalPriceTypeId);
    setSelectedProduct(variantRecord);
    setSelectedUnit(baseUnit);
    message.success('Ürün fişe eklendi');
  };

  // Satıra tıklayınca otomatik seçim yap
  const handleRowClick = (record: TableRowData) => {
    if (isParentRow(record)) {
      return;
    }

    if (isAlreadyAdded(record)) {
      message.warning('Bu ürün zaten fişte mevcut');
      return;
    }

    // Base unit'i bul veya ilk unit'i seç
    const baseUnit = record.units.find(u => u.id === record.baseUnitId) || record.units[0];
    
    if (!baseUnit) {
      message.error('Ürün birimi bulunamadı!');
      return;
    }

    // Direkt seçim yap ve modal'ı kapat
    // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
    const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
    onSelect(record, baseUnit, finalPriceTypeId);
    onClose();
    resetState();
  };

  const tableData = useMemo<TableRowData[]>(() => {
    const rowData = pageProducts.flatMap((product) => mapPageProductToRows(product));

    const productsWithVariants = new Set<number>();
    rowData.forEach((item) => {
      if (item.variantId && item.variantId !== 0) {
        productsWithVariants.add(item.productId);
      }
    });

    const singles: ProductRowDataWithType[] = [];
    const grouped = new Map<number, ParentRowData>();

    rowData.forEach((item) => {
      if (productsWithVariants.has(item.productId)) {
        let parent = grouped.get(item.productId);

        if (!parent) {
          parent = {
            key: `parent-${item.productId}`,
            rowType: 'parent',
            productId: item.productId,
            productCode: item.productCode,
            productName: item.productName,
            baseUnitId: item.baseUnitId,
            units: item.units,
            taxRate: item.taxRate,
            totalStock: 0,
            variantCount: 0,
            children: [],
            categoryId: item.categoryId,
          };
          grouped.set(item.productId, parent);
        }

        if (item.variantId && item.variantId !== 0) {
          const variantRow: ProductRowDataWithType = {
            ...item,
            rowType: 'variant',
          };

          parent.children = [...parent.children, variantRow];
          parent.totalStock += item.stock;
          parent.variantCount += 1;
        }
      } else {
        singles.push({
          ...item,
          rowType: 'single',
        });
      }
    });

    const result: TableRowData[] = [];

    grouped.forEach((parent) => {
      parent.children = parent.children
        .slice()
        .sort((a, b) => a.variantDescription.localeCompare(b.variantDescription, 'tr'));
      parent.totalStock = parent.children.reduce((sum, child) => sum + child.stock, 0);
      parent.variantCount = parent.children.length;
      result.push(parent);
    });

    result.push(...singles);

    return result;
  }, [pageProducts, mapPageProductToRows]);

  useEffect(() => {
    const parentKeys = tableData
      .filter((item): item is ParentRowData => isParentRow(item))
      .map((item) => item.key);
    setExpandedRowKeys(parentKeys);
  }, [tableData]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      const sortedCategories = buildCategoryHierarchy(data);
      setCategories(sortedCategories);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
    }
  };

  const loadUnitSets = async () => {
    try {
      const { data } = await api.get('/unitsets');
      setUnitSets(data);
    } catch (error) {
      console.error('Birim setleri yüklenirken hata:', error);
    }
  };

  const loadVariantDefinitions = async () => {
    try {
      setVariantDataLoading(true);
      const { data: variants } = await api.get('/variants');

      const valuePromises = variants.map((variant: VariantDefinition) =>
        api.get(`/variants/values/${variant.id}`)
      );

      const valueResponses = await Promise.all(valuePromises);

      const map: Record<number, VariantValueDefinition[]> = {};
      variants.forEach((variant: VariantDefinition, index: number) => {
        map[variant.id] = valueResponses[index].data;
      });

      setVariantDefinitions(variants);
      setVariantValueMap(map);

      setQuickSelectedVariants(prev => prev.map(item => {
        const variant = variants.find((v: VariantDefinition) => v.id === item.variantId);
        if (!variant) return null;
        const availableValues = map[item.variantId] || [];
        const selectedValueExists = availableValues.some((v: VariantValueDefinition) => v.id === item.selectedValueId);
        return {
          variantId: item.variantId,
          variantName: variant.name,
          selectedValueId: selectedValueExists ? item.selectedValueId : availableValues[0]?.id
        } as SelectedVariantOption;
      }).filter(Boolean) as SelectedVariantOption[]);

      setModalSelectedVariants(prev => prev.map(item => {
        const variant = variants.find((v: VariantDefinition) => v.id === item.variantId);
        if (!variant) return null;
        const availableValues = map[item.variantId] || [];
        const selectedValueExists = availableValues.some((v: VariantValueDefinition) => v.id === item.selectedValueId);
        return {
          variantId: item.variantId,
          variantName: variant.name,
          selectedValueId: selectedValueExists ? item.selectedValueId : availableValues[0]?.id
        } as SelectedVariantOption;
      }).filter(Boolean) as SelectedVariantOption[]);
    } catch (error) {
      console.error('Varyantlar yüklenirken hata:', error);
    } finally {
      setVariantDataLoading(false);
    }
  };

  const addQuickVariant = (variantId: number) => {
    const variant = variantDefinitions.find(v => v.id === variantId);
    if (!variant) return;
    if (quickSelectedVariants.some(item => item.variantId === variantId)) {
      message.warning('Bu varyant zaten eklendi');
      return;
    }
    const firstValue = variantValueMap[variantId]?.[0];
    setQuickSelectedVariants(prev => [
      ...prev,
      {
        variantId,
        variantName: variant.name,
        selectedValueId: firstValue?.id
      }
    ]);
  };

  const updateQuickVariantValue = (variantId: number, valueId?: number) => {
    setQuickSelectedVariants(prev => prev.map(item =>
      item.variantId === variantId ? { ...item, selectedValueId: valueId } : item
    ));
  };

  const removeQuickVariant = (variantId: number) => {
    setQuickSelectedVariants(prev => prev.filter(item => item.variantId !== variantId));
  };

  const addModalVariant = (variantId: number) => {
    const variant = variantDefinitions.find(v => v.id === variantId);
    if (!variant) return;
    if (modalSelectedVariants.some(item => item.variantId === variantId)) {
      message.warning('Bu varyant zaten eklendi');
      return;
    }
    const firstValue = variantValueMap[variantId]?.[0];
    setModalSelectedVariants(prev => [
      ...prev,
      {
        variantId,
        variantName: variant.name,
        selectedValueId: firstValue?.id
      }
    ]);
  };

  const updateModalVariantValue = (variantId: number, valueId?: number) => {
    setModalSelectedVariants(prev => prev.map(item =>
      item.variantId === variantId ? { ...item, selectedValueId: valueId } : item
    ));
  };

  const removeModalVariant = (variantId: number) => {
    setModalSelectedVariants(prev => prev.filter(item => item.variantId !== variantId));
  };

  const handleUnitSetChange = async (unitSetId: number) => {
    try {
      const unitSet = unitSets.find(us => us.id === unitSetId);
      if (unitSet && unitSet.units) {
        setUnits(unitSet.units);
        // İlk birimi (base unit) otomatik seç
        const baseUnit = unitSet.units.find((u: any) => u.baseRatio === 1) || unitSet.units[0];
        if (baseUnit) {
          quickAddForm.setFieldsValue({ baseUnitId: baseUnit.id });
        }
      }
    } catch (error) {
      console.error('Birimler yüklenirken hata:', error);
    }
  };

  const handleQuickAddProduct = async (values: any) => {
    try {
      setCreatingProduct(true);

      // Önce barkod ile kontrol et (eğer barkod girildiyse)
      if (values.barcode) {
        try {
          const { data: existingProduct } = await api.get(`/productbarcodes/scan/${encodeURIComponent(values.barcode)}`);
          // Ürün bulundu, direkt ekle
          message.info('Ürün zaten mevcut, tabloya ekleniyor...');
          
          const scannedUnits: Unit[] = (existingProduct.units || []).map((u: any) => ({
            id: u.id,
            name: u.name,
            symbol: u.symbol,
            baseRatio: u.baseRatio,
          }));

          const baseUnit = scannedUnits.find((u) => u.id === existingProduct.baseUnitId) || scannedUnits[0];
          if (!baseUnit) {
            message.error('Ürün birimi bulunamadı');
            return;
          }

          const effectiveDefaultTaxRate = defaultTaxRate ?? 20;

          const productData: ProductRowData = {
            key: `${existingProduct.productId}-${existingProduct.variantId || 0}`,
            productId: existingProduct.productId,
            productCode: existingProduct.productCode,
            productName: existingProduct.productName,
            variantId: existingProduct.variantId || 0,
            sku: existingProduct.sku || existingProduct.productCode,
            variantDescription: existingProduct.variantDescription || '',
            retailPrice: existingProduct.retailPrice || 0,
            wholesalePrice: existingProduct.wholesalePrice || 0,
            stock: existingProduct.stock || 0,
            units: scannedUnits,
            baseUnitId: existingProduct.baseUnitId,
            taxRate: effectiveDefaultTaxRate
          };

          // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
          const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
          onSelect(productData, baseUnit, finalPriceTypeId);
          onClose();
          resetState();
          return;
        } catch (error) {
          // Ürün bulunamadı, yeni oluştur
        }
      }

      // Yeni ürün oluştur
      const effectiveDefaultTaxRate = defaultTaxRate ?? 20;
      const isVariantProduct = values.isVariantProduct || false;
      const selectedVariantValueIds = quickSelectedVariants
        .map(item => item.selectedValueId)
        .filter((id): id is number => typeof id === 'number');

      if (isVariantProduct) {
        if (quickSelectedVariants.length === 0) {
          message.error('Varyantlı ürün için en az bir varyant türü seçmelisiniz.');
          setCreatingProduct(false);
          return;
        }

        if (selectedVariantValueIds.length !== quickSelectedVariants.length) {
          message.error('Seçtiğiniz tüm varyantlar için değer belirtmelisiniz.');
          setCreatingProduct(false);
          return;
        }
      }

      const variantRetailPrice = values.variantRetailPrice ? Number(values.variantRetailPrice) : 0;
      const variantWholesalePrice = values.variantWholesalePrice ? Number(values.variantWholesalePrice) : null;
      const variantPurchasePrice = values.variantPurchasePrice ? Number(values.variantPurchasePrice) : null;

      const productPayload = {
        productCode: 'TEMP', // Backend otomatik oluşturacak
        barcode: values.barcode || null,
        productName: values.productName,
        categoryId: values.categoryId,
        baseUnitId: values.baseUnitId,
        unitSetId: values.unitSetId,
        trackStock: true,
        taxRate: effectiveDefaultTaxRate,
        isPriceVariant: isVariantProduct,
        isWholesalePrice: isVariantProduct ? Boolean(values.variantWholesalePrice) : false,
        isPurchasePrice: isVariantProduct ? Boolean(values.variantPurchasePrice) : false
      };

      const { data: newProduct } = await api.post('/products', productPayload);
      let createdVariantId: number | null = null;
      let createdVariantData: any = null;
      let generatedVariantBarcode: string | null = null;
      
      // Barkod kaydı oluştur (eğer barkod girildiyse)
      if (values.barcode) {
        try {
          await api.post('/productbarcodes', {
            productId: newProduct.id,
            productVariantId: null,
            barcode: values.barcode,
            barcodeType: 'EAN13',
            isPrimary: true
          });
        } catch (error: any) {
          console.error('Barkod oluşturulurken hata:', error);
          // Barkod hatası kritik değil, devam et
        }
      }

      // Varyant oluştur (işaretlendiyse)
      if (isVariantProduct) {
        try {
          const variantResponse = await api.post('/productvariants', {
            productId: newProduct.id,
            price: variantRetailPrice,
            variantValueIds: selectedVariantValueIds
          });

          createdVariantData = variantResponse.data;
          createdVariantId = createdVariantData?.id;

          const priceRequests: Promise<any>[] = [];
          const nowIso = new Date().toISOString();

          if (variantRetailPrice > 0) {
            priceRequests.push(api.post('/productprices', {
              productId: newProduct.id,
              productVariantId: createdVariantId,
              priceTypeId: 1,
              price: variantRetailPrice,
              validFrom: nowIso,
              isIndefinite: true
            }));
          }

          if (variantWholesalePrice !== null && !Number.isNaN(variantWholesalePrice)) {
            priceRequests.push(api.post('/productprices', {
              productId: newProduct.id,
              productVariantId: createdVariantId,
              priceTypeId: 2,
              price: variantWholesalePrice,
              validFrom: nowIso,
              isIndefinite: true
            }));
          }

          if (variantPurchasePrice !== null && !Number.isNaN(variantPurchasePrice)) {
            priceRequests.push(api.post('/productprices', {
              productId: newProduct.id,
              productVariantId: createdVariantId,
              priceTypeId: 3,
              price: variantPurchasePrice,
              validFrom: nowIso,
              isIndefinite: true
            }));
          }

          if (priceRequests.length > 0) {
            await Promise.all(priceRequests);
          }

          if (createdVariantId) {
            const variantBarcodeValue = values.variantBarcode || generateAutoBarcode();
            generatedVariantBarcode = variantBarcodeValue;
            await api.post('/productbarcodes', {
              productId: newProduct.id,
              productVariantId: createdVariantId,
              barcode: variantBarcodeValue,
              barcodeType: 'EAN13',
              isPrimary: true
            });
          }
        } catch (error) {
          console.error('Varyant oluşturulurken hata:', error);
          message.error('Varyant oluşturulurken bir hata oluştu');
        }
      }

      await loadPagedProducts(pagination.current, pagination.pageSize);

      // Yeni oluşturulan ürünü tabloya ekle
      // Birim setinden birimleri al
      const selectedUnitSet = unitSets.find(us => us.id === values.unitSetId);
      let productUnits: any[] = [];
      
      if (selectedUnitSet && selectedUnitSet.units) {
        productUnits = selectedUnitSet.units;
      } else if (units.length > 0) {
        productUnits = units;
      } else {
        // Birim setini tekrar yükle
        try {
          const { data: unitSetData } = await api.get(`/unitsets/${values.unitSetId}`);
          if (unitSetData && unitSetData.units) {
            productUnits = unitSetData.units;
          }
        } catch (error) {
          console.error('Birim seti yüklenirken hata:', error);
        }
      }

      if (productUnits.length === 0) {
        message.error('Ürün birimleri bulunamadı!');
        return;
      }

      // Base unit'i bul
      const baseUnit = productUnits.find((u: any) => u.id === values.baseUnitId);
      
      if (!baseUnit) {
        message.error('Temel birim bulunamadı!');
        return;
      }

      // Unit formatını düzelt
      const formattedUnits: Unit[] = productUnits.map((u: any) => ({
        id: u.id,
        name: u.name || u.unitName,
        symbol: u.symbol || u.unitCode,
        baseRatio: u.baseRatio || u.conversionFactor || 1
      }));

      const productData: ProductRowData = {
        key: `${newProduct.id}-${createdVariantId ?? 0}`,
        productId: newProduct.id,
        productCode: newProduct.productCode,
        productName: newProduct.productName,
        variantId: createdVariantId ?? 0,
        sku: createdVariantData?.sku || generatedVariantBarcode || values.variantBarcode || values.barcode || newProduct.productCode,
        variantDescription: createdVariantData?.values
          ? createdVariantData.values.map((val: any) => `${val.variantName}: ${val.variantValue}`).join(', ')
          : quickSelectedVariants
              .map(sel => {
                const variant = variantDefinitions.find(v => v.id === sel.variantId);
                const match = variantValueMap[sel.variantId]?.find(v => v.id === sel.selectedValueId);
                if (variant && match) {
                  return `${variant.name}: ${match.value}`;
                }
                return '';
              })
              .filter(Boolean)
              .join(', '),
        retailPrice: createdVariantId ? (variantRetailPrice || 0) : 0,
        wholesalePrice: createdVariantId ? (variantWholesalePrice ?? 0) : 0,
        stock: createdVariantData?.stock || 0,
        units: formattedUnits,
        baseUnitId: values.baseUnitId,
        taxRate: newProduct.taxRate ?? effectiveDefaultTaxRate // Yeni oluşturulan ürünün KDV oranı
      };

      const formattedBaseUnit: Unit = {
        id: baseUnit.id,
        name: baseUnit.name || baseUnit.unitName,
        symbol: baseUnit.symbol || baseUnit.unitCode,
        baseRatio: baseUnit.baseRatio || baseUnit.conversionFactor || 1
      };

      // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
      const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
      onSelect(productData, formattedBaseUnit, finalPriceTypeId);
      
      message.success('Ürün oluşturuldu ve tabloya eklendi');
      onClose();
      resetState();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ürün oluşturulurken hata oluştu');
    } finally {
      setCreatingProduct(false);
    }
  };

  const openVariantModal = async (product: ProductRowData, sourceVariantId?: number) => {
    setVariantModalProduct(product);
    setVariantModalVisible(true);
    setVariantModalLoading(false);
    setVariantModalStock(null);
    variantModalForm.resetFields();

    let existingVariantDetails: any | undefined;

    const productInfo = productInfos[product.productId];
    const retailPrice = product.retailPrice || 0;

    const variantIdToUse = sourceVariantId || product.variantId;

    if (variantIdToUse) {
      try {
        const variantResponse = await api.get(`/productvariants/by-product/${product.productId}`);
        const variantData = (variantResponse.data || []).find((item: any) => item.id === variantIdToUse);
        if (variantData) {
          existingVariantDetails = variantData;
        }
      } catch (error) {
        console.error('Mevcut varyant bilgileri alınırken hata:', error);
      }
    }

    const wholesalePrice = existingVariantDetails?.wholesalePrice ?? product.wholesalePrice ?? null;

    let purchasePrice = null;
    try {
      if (variantIdToUse) {
        const priceResponse = await api.get('/productprices');
        const prices: any[] = priceResponse.data || [];
        const purchasePriceEntry = prices.find(p => p.productVariantId === variantIdToUse && p.priceTypeId === 3);
        if (purchasePriceEntry) {
          purchasePrice = purchasePriceEntry.price;
        }
      }
    } catch (error) {
      console.error('Varyant fiyat bilgileri alınırken hata:', error);
    }

    variantModalForm.setFieldsValue({
      variantRetailPrice: retailPrice,
      variantWholesalePrice: wholesalePrice,
      variantPurchasePrice: purchasePrice,
      variantBarcode: existingVariantDetails?.primaryBarcode || '',
      transferStock: false
    });

    const initialModalVariants: SelectedVariantOption[] = existingVariantDetails?.values?.length
      ? existingVariantDetails.values.map((val: any) => ({
          variantId: val.variantId,
          variantName: val.variantName,
          selectedValueId: val.variantValueId
        }))
      : variantDefinitions.length > 0
        ? [{
            variantId: variantDefinitions[0].id,
            variantName: variantDefinitions[0].name,
            selectedValueId: variantValueMap[variantDefinitions[0].id]?.[0]?.id
          }]
        : [];

    setModalSelectedVariants(initialModalVariants);
  };

  const closeVariantModal = () => {
    setVariantModalVisible(false);
    setVariantModalProduct(null);
    setVariantModalStock(null);
    variantModalForm.resetFields();
    setModalSelectedVariants([]);
  };

  const handleVariantModalSubmit = async () => {
    if (!variantModalProduct) return;

    try {
      const values = await variantModalForm.validateFields();
      setVariantModalLoading(true);

      if (variantDefinitions.length === 0) {
        message.warning('Önce varyant tanımları oluşturmalısınız.');
        setVariantModalLoading(false);
        return;
      }

      const selectedVariantValueIds = modalSelectedVariants
        .map(item => item.selectedValueId)
        .filter((id): id is number => typeof id === 'number');

      if (modalSelectedVariants.length === 0) {
        message.error('En az bir varyant türü seçmelisiniz.');
        setVariantModalLoading(false);
        return;
      }

      if (selectedVariantValueIds.length !== modalSelectedVariants.length) {
        message.error('Seçilen tüm varyantlar için değer belirtmelisiniz.');
        setVariantModalLoading(false);
        return;
      }

      const retailPrice = values.variantRetailPrice ? Number(values.variantRetailPrice) : 0;
      const wholesalePrice = values.variantWholesalePrice ? Number(values.variantWholesalePrice) : null;
      const purchasePrice = values.variantPurchasePrice ? Number(values.variantPurchasePrice) : null;

      const variantResponse = await api.post('/productvariants', {
        productId: variantModalProduct.productId,
        price: retailPrice,
        variantValueIds: selectedVariantValueIds
      });

      const newVariant = variantResponse.data;

      const priceRequests: Promise<any>[] = [];
      const nowIso = new Date().toISOString();

      if (retailPrice > 0) {
        priceRequests.push(api.post('/productprices', {
          productId: variantModalProduct.productId,
          productVariantId: newVariant.id,
          priceTypeId: 1,
          price: retailPrice,
          validFrom: nowIso,
          isIndefinite: true
        }));
      }

      if (wholesalePrice !== null && !Number.isNaN(wholesalePrice)) {
        priceRequests.push(api.post('/productprices', {
          productId: variantModalProduct.productId,
          productVariantId: newVariant.id,
          priceTypeId: 2,
          price: wholesalePrice,
          validFrom: nowIso,
          isIndefinite: true
        }));
      }

      if (purchasePrice !== null && !Number.isNaN(purchasePrice)) {
        priceRequests.push(api.post('/productprices', {
          productId: variantModalProduct.productId,
          productVariantId: newVariant.id,
          priceTypeId: 3,
          price: purchasePrice,
          validFrom: nowIso,
          isIndefinite: true
        }));
      }

      if (priceRequests.length > 0) {
        await Promise.all(priceRequests);
      }

      const modalVariantBarcode = values.variantBarcode || generateAutoBarcode();
      await api.post('/productbarcodes', {
        productId: variantModalProduct.productId,
        productVariantId: newVariant.id,
        barcode: modalVariantBarcode,
        barcodeType: 'EAN13',
        isPrimary: true
      });

      if (values.transferStock && variantModalStock && variantModalStock > 0) {
        try {
          await api.post('/productvariants/distribute-stock', {
            productId: variantModalProduct.productId,
            distributions: [
              {
                variantId: newVariant.id,
                quantity: variantModalStock
              }
            ]
          });
        } catch (error) {
          console.error('Stok dağıtımı sırasında hata:', error);
          message.error('Stok aktarımı sırasında bir hata oluştu.');
        }
      }

      await loadPagedProducts(pagination.current, pagination.pageSize);

      const productInfo = productInfos[variantModalProduct.productId];
      const unitsForProduct = productInfo?.units || variantModalProduct.units;
      const baseUnit = unitsForProduct.find((u: any) => u.id === (productInfo?.baseUnitId || variantModalProduct.baseUnitId));

      const variantDescription = newVariant.values
        ? newVariant.values.map((val: any) => `${val.variantName}: ${val.variantValue}`).join(', ')
        : modalSelectedVariants
            .map(sel => {
              const variant = variantDefinitions.find(v => v.id === sel.variantId);
              const match = variantValueMap[sel.variantId]?.find(v => v.id === sel.selectedValueId);
              if (variant && match) {
                return `${variant.name}: ${match.value}`;
              }
              return '';
            })
            .filter(Boolean)
            .join(', ');

      const productData: ProductRowData = {
        key: `${variantModalProduct.productId}-${newVariant.id}`,
        productId: variantModalProduct.productId,
        productCode: variantModalProduct.productCode,
        productName: variantModalProduct.productName,
        variantId: newVariant.id,
        sku: newVariant.sku || modalVariantBarcode || variantModalProduct.sku,
        variantDescription,
        retailPrice: retailPrice || variantModalProduct.retailPrice,
        wholesalePrice: wholesalePrice ?? variantModalProduct.wholesalePrice,
        stock: newVariant.stock || 0,
        units: unitsForProduct,
        baseUnitId: productInfo?.baseUnitId || variantModalProduct.baseUnitId,
        taxRate: productInfo?.taxRate ?? defaultTaxRate ?? 20
      };

      const finalBaseUnit = baseUnit || variantModalProduct.units[0];
      // Document type'a göre belirlenen priceTypeId'yi kullan, yoksa customerCategory'ye göre belirle
      const finalPriceTypeId = priceTypeId ?? (customerCategory === 'Retail' ? 1 : 2);
      onSelect(productData, finalBaseUnit, finalPriceTypeId);

      message.success('Varyant oluşturuldu');
      closeVariantModal();
    } catch (error) {
      console.error('Varyant eklenirken hata:', error);
    } finally {
      setVariantModalLoading(false);
    }
  };

  const openVariantDrawer = (type: 'definition' | 'value', primaryVariantId?: number | null) => {
    setVariantDrawerType(type);
    setVariantDrawerPrimaryVariantId(primaryVariantId ?? null);
    const initialValues: any = {};
    if (type === 'value') {
      initialValues.variantId = primaryVariantId ?? undefined;
    }
    variantDrawerForm.resetFields();
    variantDrawerForm.setFieldsValue(initialValues);
    setIsVariantDrawerVisible(true);
  };

  const handleVariantDrawerClose = () => {
    setIsVariantDrawerVisible(false);
    setVariantDrawerType('definition');
    setVariantDrawerPrimaryVariantId(null);
    variantDrawerForm.resetFields();
  };

  const handleVariantDrawerSubmit = async () => {
    try {
      const values = await variantDrawerForm.validateFields();

      if (variantDrawerType === 'definition') {
        const { code, name } = values;

        const { data: newVariant } = await api.post('/variants', { code, name });
        message.success('Ana varyant oluşturuldu');

        setVariantDefinitions(prev => [...prev, newVariant]);
        setVariantValueMap(prev => ({ ...prev, [newVariant.id]: [] }));

        setQuickSelectedVariants(prev => {
          if (prev.some(item => item.variantId === newVariant.id)) return prev;
          return [...prev, { variantId: newVariant.id, variantName: newVariant.name }];
        });

        setModalSelectedVariants(prev => {
          if (prev.some(item => item.variantId === newVariant.id)) return prev;
          return [...prev, { variantId: newVariant.id, variantName: newVariant.name }];
        });
      } else {
        const { variantId, code, value } = values;
        const targetVariantId = variantId || variantDrawerPrimaryVariantId;
        if (!targetVariantId) {
          message.error('Varyant değeri eklemek için önce ana varyant seçmelisiniz.');
          return;
        }

        const { data: newValue } = await api.post('/variants/values', {
          variantId: targetVariantId,
          code,
          value
        });
        message.success('Varyant değeri oluşturuldu');

        setVariantValueMap(prev => ({
          ...prev,
          [targetVariantId]: [...(prev[targetVariantId] || []), {
            id: newValue.id,
            variantId: newValue.variantId,
            value: newValue.value,
            code: newValue.code
          }]
        }));

        const targetVariant = variantDefinitions.find(v => v.id === targetVariantId);

        setQuickSelectedVariants(prev => {
          const exists = prev.find(item => item.variantId === targetVariantId);
          if (exists) {
            return prev.map(item => item.variantId === targetVariantId ? { ...item, selectedValueId: newValue.id } : item);
          }
          if (!targetVariant) return prev;
          return [...prev, { variantId: targetVariantId, variantName: targetVariant.name, selectedValueId: newValue.id }];
        });

        setModalSelectedVariants(prev => {
          const exists = prev.find(item => item.variantId === targetVariantId);
          if (exists) {
            return prev.map(item => item.variantId === targetVariantId ? { ...item, selectedValueId: newValue.id } : item);
          }
          if (!targetVariant) return prev;
          return [...prev, { variantId: targetVariantId, variantName: targetVariant.name, selectedValueId: newValue.id }];
        });
      }

      handleVariantDrawerClose();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error.response?.data?.message || 'İşlem sırasında bir hata oluştu');
    }
  };

  const resetState = () => {
    setSearchText('');
    setSelectedProduct(null);
    setSelectedUnit(null);
    setShowQuickAdd(false);
    quickAddForm.resetFields();
    setQuickSelectedVariants([]);
    setSelectedCategoryId(null);
    setSelectedProductBrandId(null);
    setInStockOnly(true);
    setPageProducts([]);
  };

  const handleParentAddVariant = (parent: ParentRowData) => {
    const baseRow = parent.children[0];
    if (baseRow) {
      openVariantModal(baseRow, baseRow.variantId || undefined);
    } else {
      const fallback: ProductRowData = {
        key: `${parent.productId}-0`,
        productId: parent.productId,
        productCode: parent.productCode,
        productName: parent.productName,
        variantId: 0,
        sku: parent.productCode,
        variantDescription: '',
        retailPrice: 0,
        wholesalePrice: 0,
        stock: parent.totalStock,
        units: parent.units,
        baseUnitId: parent.baseUnitId,
        taxRate: parent.taxRate,
        categoryId: parent.categoryId,
      };
      openVariantModal(fallback, undefined);
    }
  };

  const columns: ColumnsType<TableRowData> = [
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
      render: (_: any, record) => record.productCode,
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      render: (text: string, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          {record.rowType !== 'parent' && (
            <div style={{ fontSize: '12px', color: '#666' }}>{record.productCode}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Varyant',
      dataIndex: 'variantDescription',
      key: 'variantDescription',
      width: 200,
      render: (_: any, record) => {
        if (isParentRow(record)) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span>Varyant sayısı: {record.variantCount}</span>
                <Button
                  size="small"
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleParentAddVariant(record);
                  }}
                  disabled={variantDefinitions.length === 0}
                />
              </div>
              <span style={{ fontSize: 12, color: '#888' }}>Toplam stok: {record.totalStock}</span>
            </div>
          );
        }

        const row = record as ProductRowDataWithType;
        const variantText = row.variantId === 0 ? 'Standart' : row.variantDescription;

        return (
          <div>
            <div style={{ fontSize: '12px', color: '#666' }}>SKU: {row.sku}</div>
            <span>{variantText || 'Standart'}</span>
          </div>
        );
      },
    },
    ...(canSeePurchasePrice
      ? [
          {
            title: 'Alış Fiyatı',
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            width: 100,
            render: (price: number | undefined, record: TableRowData) => (
              isParentRow(record) ? '-' : (price ? `${baseCurrencySymbol}${price.toFixed(2)}` : '-')
            ),
          },
        ]
      : []),
    {
      title: 'Perakende Fiyat',
      dataIndex: 'retailPrice',
      key: 'retailPrice',
      width: 120,
      render: (price: number | undefined, record) => (
        isParentRow(record) ? '-' : (price ? `${baseCurrencySymbol}${price.toFixed(2)}` : '-')
      ),
    },
    {
      title: 'Toptan Fiyat',
      dataIndex: 'wholesalePrice',
      key: 'wholesalePrice',
      width: 120,
      render: (price: number | undefined, record) => (
        isParentRow(record) ? '-' : (price ? `${baseCurrencySymbol}${price.toFixed(2)}` : '-')
      ),
    },
    {
      title: 'Stok',
      dataIndex: 'stock',
      key: 'stock',
      width: 80,
      render: (stock: number, record) => {
        const value = isParentRow(record) ? record.totalStock : stock;
        return (
          <Tag color={value > 0 ? 'green' : 'red'}>
            {value}
          </Tag>
        );
      },
    },
    {
      title: 'İşlem',
      key: 'actions',
      width: 80,
      render: (_: any, record) => {
        if (isParentRow(record)) {
          return null;
        }

        const row = record as ProductRowDataWithType;

        return (
          <Tooltip title={isAlreadyAdded(row) ? 'Bu ürün fişte mevcut' : 'Fişe ekle'}>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleAddWithoutClosing(row);
              }}
              disabled={isAlreadyAdded(row)}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Modal
      title="Ürün Seçin"
      open={visible}
      onCancel={() => {
        onClose();
        resetState();
      }}
      width={1100}
      footer={[
        <Button key="cancel" onClick={onClose}>
          İptal
        </Button>,
        !showQuickAdd && (
          <Button 
            key="select" 
            type="primary" 
            onClick={handleSelect}
            disabled={!selectedProduct || !selectedUnit}
          >
            Seç
          </Button>
        ),
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {productInfosError && (
          <Alert
            type="error"
            showIcon
            message="Ürün listesi yüklenemedi"
            description={productInfosError}
            action={
              <Button size="small" onClick={() => loadPagedProducts(pagination.current, pagination.pageSize)}>
                Tekrar dene
              </Button>
            }
          />
        )}
        <Row gutter={16} align="middle">
          <Col span={5}>
            <Select
              value={searchType}
              onChange={setSearchType}
              style={{ width: '100%' }}
            >
              <Select.Option value="name">Ürün Adı</Select.Option>
              <Select.Option value="code">Ürün Kodu</Select.Option>
              <Select.Option value="barcode">Barkod (SKU)</Select.Option>
            </Select>
          </Col>
          <Col span={5}>
            <Select
              value={selectedCategoryId ?? undefined}
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Kategori filtrele"
              style={{ width: '100%' }}
              onChange={(value) => setSelectedCategoryId(typeof value === 'number' ? value : null)}
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.displayName}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <Select
              value={selectedProductBrandId ?? undefined}
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Ürün markası filtrele"
              style={{ width: '100%' }}
              onChange={(value) => setSelectedProductBrandId(typeof value === 'number' ? value : null)}
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {productBrands.map(b => (
                <Select.Option key={b.id} value={b.id}>
                  {b.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={5}>
            <Input
              placeholder={`${searchType === 'code' ? 'Ürün kodu' : searchType === 'barcode' ? 'Barkod/SKU' : 'Ürün adı'} ile ara...`}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Space>
              <Switch
                checked={inStockOnly}
                onChange={setInStockOnly}
              />
              <span>Stoktakiler</span>
            </Space>
          </Col>
        </Row>

        {priceTypeId && (priceTypeId !== 3 || canSeePurchasePrice) && (
          <Tag color={priceTypeId === 1 ? 'gold' : priceTypeId === 2 ? 'cyan' : 'orange'}>
            {priceTypeId === 1 ? '🏪 Perakende Fiyat Uygulanacak' : priceTypeId === 2 ? '📦 Toptan Fiyat Uygulanacak' : '💰 Alış Fiyatı Uygulanacak'}
          </Tag>
        )}
        {!priceTypeId && customerCategory && (
          <Tag color={customerCategory === 'Retail' ? 'gold' : 'cyan'}>
            {customerCategory === 'Retail' ? '🏪 Perakende Fiyat Uygulanacak' : '📦 Toptan Fiyat Uygulanacak'}
          </Tag>
        )}

        {showQuickAdd ? (
          <div style={{ padding: '20px', border: '1px dashed #d9d9d9', borderRadius: '6px', backgroundColor: '#fafafa' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Tag color="orange">⚠️ Ürün bulunamadı</Tag>
                <span style={{ marginLeft: 8 }}>Yeni ürün oluşturmak ister misiniz?</span>
              </div>
              
              <Form
                form={quickAddForm}
                layout="vertical"
                onFinish={handleQuickAddProduct}
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="productCode"
                      label="Ürün Kodu (Opsiyonel)"
                      tooltip="Boş bırakılırsa otomatik oluşturulur"
                    >
                      <Input placeholder="GIRTO-5570" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="barcode"
                      label="Barkod"
                    >
                      <Input placeholder="8681234565570" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="productName"
                      label="Ürün Adı"
                      rules={[{ required: true, message: 'Ürün adı giriniz' }]}
                    >
                      <Input placeholder="Taşlı / Girto / Vücut Çorabı" />
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      name="categoryId"
                      label="Kategori"
                      rules={[{ required: true, message: 'Kategori seçiniz' }]}
                    >
                      <Select placeholder="Kategori seçiniz">
                        {categories.map(cat => (
                          <Select.Option
                            key={cat.id}
                            value={cat.id}
                            disabled={cat.hasChildren}
                          >
                            {cat.displayName}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="unitSetId"
                      label="Birim Seti"
                      rules={[{ required: true, message: 'Birim seti seçiniz' }]}
                    >
                      <Select 
                        placeholder="Birim seti seçiniz"
                        onChange={handleUnitSetChange}
                      >
                        {unitSets.map(us => (
                          <Select.Option key={us.id} value={us.id}>
                            {us.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      name="baseUnitId"
                      label="Temel Birim"
                      rules={[{ required: true, message: 'Temel birim seçiniz' }]}
                    >
                      <Select placeholder="Temel birim seçiniz">
                        {units.map(unit => (
                          <Select.Option key={unit.id} value={unit.id}>
                            {unit.name} ({unit.symbol})
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider orientation="left">Varyant Ayarları</Divider>

                <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={8}>
                    <Form.Item
                      name="isVariantProduct"
                      valuePropName="checked"
                      initialValue={false}
                      label="Varyantlı ürün"
                    >
                      <Switch disabled={variantDataLoading} />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    {quickAddIsVariantProduct && (
                      <Tag color="processing">Varyant seçeneklerini doldurabilirsiniz</Tag>
                    )}
                  </Col>
                </Row>

                {quickAddIsVariantProduct && (
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {variantDataLoading ? (
                      <Alert type="info" message="Varyant verileri yükleniyor..." />
                    ) : (
                      <>
                        {variantDefinitions.length === 0 ? (
                          <Alert type="warning" message="Tanımlı varyant bulunamadı. Varyant eklemek için önce varyant tanımları oluşturun." />
                        ) : (
                          <>
                            <Row gutter={8} align="middle">
                              <Col span={12}>
                                <Select
                                  allowClear
                                  placeholder="Varyant ekleyin"
                                  options={variantDefinitions
                                    .filter(variant => !quickSelectedVariants.some(item => item.variantId === variant.id))
                                    .map(variant => ({
                                      label: variant.name,
                                      value: variant.id,
                                      disabled: (variantValueMap[variant.id] || []).length === 0
                                    }))}
                                  onChange={(value) => {
                                    if (typeof value === 'number') {
                                      addQuickVariant(value);
                                    }
                                  }}
                                  style={{ width: '100%' }}
                                />
                              </Col>
                              <Col span={12}>
                                <Button
                                  icon={<PlusOutlined />}
                                  onClick={() => openVariantDrawer('definition')}
                                  style={{ width: '100%' }}
                                >
                                  Yeni Ana Varyant
                                </Button>
                              </Col>
                            </Row>

                            {quickSelectedVariants.map(item => (
                              <Row key={item.variantId} gutter={8} align="middle">
                                <Col span={6}>
                                  <Tag color="blue" style={{ width: '100%', textAlign: 'center' }}>{item.variantName}</Tag>
                                </Col>
                                <Col span={10}>
                                  <Select
                                    placeholder="Varyant değeri seçin"
                                    value={item.selectedValueId}
                                    options={(variantValueMap[item.variantId] || []).map(value => ({
                                      label: value.value,
                                      value: value.id
                                    }))}
                                    onChange={(value) => updateQuickVariantValue(item.variantId, value as number)}
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                                <Col span={4}>
                                  <Button
                                    icon={<PlusOutlined />}
                                    onClick={() => openVariantDrawer('value', item.variantId)}
                                    style={{ width: '100%' }}
                                  >
                                    Değer
                                  </Button>
                                </Col>
                                <Col span={4}>
                                  <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeQuickVariant(item.variantId)}
                                    style={{ width: '100%' }}
                                  />
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
 
                        <Row gutter={16}>
                          <Col span={6}>
                            <Form.Item
                              name="variantRetailPrice"
                              label="Satış Fiyatı"
                              rules={[{ required: true, message: 'Satış fiyatı giriniz' }]}
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                placeholder="0.00"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={6}>
                            <Form.Item
                              name="variantWholesalePrice"
                              label="Toptan Fiyatı"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                placeholder="0.00"
                              />
                            </Form.Item>
                          </Col>
                          {canSeePurchasePrice && (
                          <Col span={6}>
                            <Form.Item
                              name="variantPurchasePrice"
                              label="Alış Fiyatı"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={0.01}
                                placeholder="0.00"
                              />
                            </Form.Item>
                          </Col>
                          )}
                          <Col span={6}>
                            <Form.Item
                              name="variantBarcode"
                              label="Varyant Barkodu"
                            >
                              <Input placeholder="Barkod" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Space>
                )}
 
                <Form.Item>
                  <Space>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      icon={<PlusOutlined />}
                      loading={creatingProduct}
                    >
                      Oluştur ve Ekle
                    </Button>
                    <Button onClick={() => {
                      setShowQuickAdd(false);
                      setSearchText('');
                      quickAddForm.resetFields();
                    }}>
                      İptal
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </div>
        ) : (
          <>
            {selectedProduct && (
              <Row gutter={16}>
                <Col span={12}>
                  <strong>Seçili Ürün:</strong> {selectedProduct.productName} ({selectedProduct.sku})
                </Col>
                <Col span={12}>
                  <Space style={{ width: '100%' }} align="start">
                    <Select
                      placeholder="Birim seçin"
                      style={{ width: '100%' }}
                      value={selectedUnit?.id}
                      onChange={(unitId) => {
                        const unit = selectedProduct.units.find(u => u.id === unitId);
                        setSelectedUnit(unit || null);
                      }}
                    >
                      {selectedProduct.units.map(unit => (
                        <Select.Option key={unit.id} value={unit.id}>
                          {unit.name} ({unit.symbol})
                        </Select.Option>
                      ))}
                    </Select>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => openVariantModal(selectedProduct, selectedProduct.variantId || undefined)}
                      disabled={variantDefinitions.length === 0}
                    >
                      Varyant Ekle
                    </Button>
                  </Space>
                </Col>
              </Row>
            )}
            
            <Table
              columns={columns}
              dataSource={tableData}
              rowKey="key"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Toplam ${total} ürün`,
              }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedProduct ? [selectedProduct.key] : [],
                onChange: (_, selectedRows) => {
                  const row = (selectedRows[0] as TableRowData | undefined) || null;
                  if (row && !isParentRow(row)) {
                    const product = row as ProductRowDataWithType;
                    setSelectedProduct(product);
                    const baseUnit = product.units.find(u => u.id === product.baseUnitId);
                    setSelectedUnit(baseUnit || product.units[0] || null);
                  } else {
                    setSelectedProduct(null);
                    setSelectedUnit(null);
                  }
                },
                getCheckboxProps: (record) => {
                  if (isParentRow(record)) {
                    return { disabled: true };
                  }
                  return {
                    disabled: isAlreadyAdded(record),
                  };
                },
              }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
                expandRowByClick: false,
              }}
              onChange={(pag) => {
                const nextPageSize = pag.pageSize ?? pagination.pageSize;
                const pageSizeChanged = nextPageSize !== pagination.pageSize;
                const nextPage = pageSizeChanged ? 1 : (pag.current ?? pagination.current);
                setPagination((prev) => ({
                  ...prev,
                  current: nextPage,
                  pageSize: nextPageSize,
                }));
                loadPagedProducts(nextPage, nextPageSize);
              }}
              onRow={(record) => ({
                onClick: () => {
                  if (!isParentRow(record)) {
                    handleRowClick(record);
                  }
                },
                style: { 
                  cursor: isParentRow(record) ? 'default' : 'pointer',
                  backgroundColor: !isParentRow(record) && isAlreadyAdded(record) ? '#fffbe6' : undefined
                }
              })}
              locale={{
                emptyText: searchText || selectedCategoryId || selectedProductBrandId
                  ? 'Ürün bulunamadı'
                  : 'Ürün listesi yükleniyor'
              }}
            />
          </>
        )}
      </Space>
      
      <Modal
        title={variantModalProduct ? `${variantModalProduct.productName} için Varyant Ekle` : 'Varyant Ekle'}
        open={variantModalVisible}
        onCancel={closeVariantModal}
        onOk={handleVariantModalSubmit}
        confirmLoading={variantModalLoading}
        width={700}
        destroyOnClose
      >
        <Form
          form={variantModalForm}
          layout="vertical"
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={variantModalProduct ? `${variantModalProduct.productCode} - ${variantModalProduct.productName}` : 'Ürün seçilmedi'}
            description={variantModalStock !== null ? `Ana ürün stok toplamı: ${variantModalStock}` : 'Ana ürün stok bilgisi alınamadı'}
          />

          {variantDefinitions.length === 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Varyant tanımı bulunamadı"
              description="Varyant ekleyebilmek için önce varyant ve varyant değerlerini tanımlamalısınız."
            />
          )}

          {variantDefinitions.length === 0 ? (
            <Alert type="warning" message="Varyant tanımı bulunamadı. Önce varyant ekleyiniz." />
          ) : (
            <>
              <Row gutter={8} align="middle">
                <Col span={12}>
                  <Select
                    allowClear
                    placeholder="Varyant ekleyin"
                    options={variantDefinitions
                      .filter(variant => !modalSelectedVariants.some(item => item.variantId === variant.id))
                      .map(variant => ({
                        label: variant.name,
                        value: variant.id,
                        disabled: (variantValueMap[variant.id] || []).length === 0
                      }))}
                    onChange={(value) => {
                      if (typeof value === 'number') {
                        addModalVariant(value);
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col span={12}>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => openVariantDrawer('definition')}
                    style={{ width: '100%' }}
                  >
                    Yeni Ana Varyant
                  </Button>
                </Col>
              </Row>

              {modalSelectedVariants.map(item => (
                <Row key={item.variantId} gutter={8} align="middle">
                  <Col span={6}>
                    <Tag color="blue" style={{ width: '100%', textAlign: 'center' }}>{item.variantName}</Tag>
                  </Col>
                  <Col span={10}>
                    <Select
                      placeholder="Varyant değeri seçin"
                      value={item.selectedValueId}
                      options={(variantValueMap[item.variantId] || []).map(value => ({
                        label: value.value,
                        value: value.id
                      }))}
                      onChange={(value) => updateModalVariantValue(item.variantId, value as number)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={4}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => openVariantDrawer('value', item.variantId)}
                      style={{ width: '100%' }}
                    >
                      Değer
                    </Button>
                  </Col>
                  <Col span={4}>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeModalVariant(item.variantId)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              ))}
            </>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="variantRetailPrice"
                label="Satış Fiyatı"
                rules={[{ required: true, message: 'Satış fiyatı giriniz' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="variantWholesalePrice"
                label="Toptan Fiyatı"
              >
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
              </Form.Item>
            </Col>
            {canSeePurchasePrice && (
            <Col span={8}>
              <Form.Item
                name="variantPurchasePrice"
                label="Alış Fiyatı"
              >
                <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
              </Form.Item>
            </Col>
          )}
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="variantBarcode"
                label="Varyant Barkodu"
              >
                <Input placeholder="Barkod" />
              </Form.Item>
            </Col>
            <Col span={12}>
              {variantModalStock && variantModalStock > 0 && (
                <Form.Item
                  name="transferStock"
                  valuePropName="checked"
                  label="Stok aktarımı"
                >
                  <Switch
                    checkedChildren={`Ana stoktan aktar (${variantModalStock})`}
                    unCheckedChildren="Stok aktarma"
                  />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={variantDrawerType === 'definition' ? 'Yeni Ana Varyant' : 'Yeni Varyant Değeri'}
        open={isVariantDrawerVisible}
        onCancel={handleVariantDrawerClose}
        onOk={handleVariantDrawerSubmit}
        destroyOnClose
      >
        <Form
          form={variantDrawerForm}
          layout="vertical"
        >
          {variantDrawerType === 'definition' ? (
            <>
              <Form.Item
                name="code"
                label="Ana Varyant Kodu"
                rules={[{ required: true, message: 'Kod giriniz' }]}
              >
                <Input placeholder="Örn. RENK" />
              </Form.Item>
              <Form.Item
                name="name"
                label="Ana Varyant Adı"
                rules={[{ required: true, message: 'Ad giriniz' }]}
              >
                <Input placeholder="Örn. Renk" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="variantId"
                label="Ana Varyant"
                initialValue={variantDrawerPrimaryVariantId ?? undefined}
                rules={[{ required: true, message: 'Ana varyant seçiniz' }]}
              >
                <Select
                  placeholder="Ana varyant seçin"
                  options={variantDefinitions.map(variant => ({
                    label: variant.name,
                    value: variant.id
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="code"
                label="Varyant Değer Kodu"
                rules={[{ required: true, message: 'Kod giriniz' }]}
              >
                <Input placeholder="Örn. SIYAH" />
              </Form.Item>
              <Form.Item
                name="value"
                label="Varyant Değer Adı"
                rules={[{ required: true, message: 'Ad giriniz' }]}
              >
                <Input placeholder="Örn. Siyah" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </Modal>
  );
};

export default ProductSelectModal;

